// Phase 6.3 C — BatchModal «Восполнение» mode form.
//
// Self-contained sibling of the legacy random / station_codes form.
// Owns the source picker (radio: «Все даты с DONE» / «По сериям» /
// «По кодам станций»), the optional date-range, the target_configs
// list, the include_failed_categories checkboxes (with the two
// deterministic categories greyed out per spec §C.1), the debounced
// plan-preview fetch, and the «Запустить» submit button.
//
// The parent (`BatchModal`) just renders this component when the
// mode toggle is on «Восполнение»; it doesn't share state with the
// legacy random/stations branch, which keeps the type ergonomics
// simple (no `Mode | null` unions in either path).
//
// Spec §C.4: `auto_substitute=false` by default — backfill targets
// specific (station, date, config) tuples, not alternative
// stations. The form doesn't expose the auto-substitute checkbox at
// all; the backend defaults it to `false` regardless.

import { useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../api/client';
import type {
  BackfillPlanResponse,
  BackfillRunResponse,
  FailedCategory,
} from '../api/types';
import { useDebounce } from '../hooks/useDebounce';

/** Spec §C.1 — `rinex_real_missing` and `rinex_not_available` are
 *  greyed-out in the UI because re-running them is deterministic
 *  (catalog reality wouldn't change). The other four FAILED v2
 *  categories are operator-opt-in. */
const RETRYABLE_FAILED_CATEGORIES: ReadonlyArray<{
  value: FailedCategory;
  label: string;
}> = [
  { value: 'rinex_parse_error', label: 'rinex_parse_error' },
  { value: 'compute_failed_ambiguity', label: 'compute_failed_ambiguity' },
  { value: 'service_compute_failed', label: 'service_compute_failed' },
  { value: 'other', label: 'other' },
] as const;

const DETERMINISTIC_FAILED_CATEGORIES: ReadonlyArray<{
  value: FailedCategory;
  label: string;
}> = [
  { value: 'rinex_real_missing', label: 'rinex_real_missing' },
  { value: 'rinex_not_available', label: 'rinex_not_available' },
] as const;

/** Spec §C.1 «Какие конфиги восполнять» — five canonical keys the
 *  operator can choose from. The keys match the analytics surface +
 *  the planner's `canonical_config_key` output exactly. */
const TARGET_CONFIG_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: 'gps', label: 'GPS' },
  { value: 'glonass', label: 'ГЛО' },
  { value: 'galileo', label: 'GAL' },
  { value: 'beidou', label: 'BDS' },
  { value: 'beidou,galileo,glonass,gps', label: 'все 4' },
] as const;

type SourceType = 'all_dates_with_done' | 'specific_tags' | 'station_codes';

/** Phase 6.3.1 C — Russian labels for the planner's
 *  `breakdown_by_reason_for_excluded` keys. Unknown reasons fall
 *  through to the raw key so a future planner extension still
 *  renders something. */
const EXCLUSION_REASON_LABELS: Readonly<Record<string, string>> = {
  already_done: 'уже посчитаны',
  already_pending: 'уже в очереди',
  rinex_not_available: 'RINEX недоступен (кэш)',
  plate_outside: 'станция вне плиты (кэш)',
  compute_failed_with_data: 'PPP сломался (кэш)',
  // Phase 6.3.1 — pre-flight RINEX subset rejection (cache row written
  // by scheduler/_flow.py after a failed validator call).
  systems_partial_missing: 'отклонены до PPP (нет нужных систем)',
};

/** Estimate-duration formatter — turns minutes into «X ч YY мин» so
 *  the preview pane reads naturally. */
function formatDuration(min: number): string {
  if (min <= 0) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} мин`;
  if (m === 0) return `${h} ч`;
  return `${h} ч ${m} мин`;
}

/** Auto-generated tag in the spec §C.1 shape:
 *  `Backfill_<source_summary>_<yyyy-mm-dd_HHMM>`. */
function defaultTag(source: SourceType, summary: string): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const stamp = `${yyyy}-${mm}-${dd}_${hh}${min}`;
  const sourceLabel =
    source === 'all_dates_with_done'
      ? 'all'
      : source === 'specific_tags'
        ? summary || 'tags'
        : summary || 'stations';
  return `Backfill_${sourceLabel}_${stamp}`;
}

export function BackfillModeForm({
  onCreated,
}: {
  onCreated: (resp: BackfillRunResponse) => void;
}) {
  const [sourceType, setSourceType] = useState<SourceType>(
    'all_dates_with_done',
  );
  const [tagsRaw, setTagsRaw] = useState<string>('');
  const [stationsRaw, setStationsRaw] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [targetConfigs, setTargetConfigs] = useState<string[]>([
    'gps',
    'glonass',
    'galileo',
    'beidou',
    'beidou,galileo,glonass,gps',
  ]);
  const [includeFailed, setIncludeFailed] = useState<FailedCategory[]>([]);
  const [tagInput, setTagInput] = useState<string>('');

  const tagsList = useMemo(
    () =>
      tagsRaw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [tagsRaw],
  );
  const stationsList = useMemo(
    () =>
      stationsRaw
        .split(/[\s,]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean),
    [stationsRaw],
  );

  // Spec §C.1 — auto-generated default tag. Updates as the source
  // changes; once the operator types into the input, we stop
  // auto-overwriting (the input becomes authoritative).
  const sourceSummary =
    sourceType === 'specific_tags'
      ? tagsList[0] ?? 'tags'
      : sourceType === 'station_codes'
        ? stationsList[0] ?? 'stations'
        : 'all';
  const computedDefaultTag = useMemo(
    () => defaultTag(sourceType, sourceSummary),
    [sourceType, sourceSummary],
  );
  const effectiveTag = tagInput.trim() || computedDefaultTag;

  // Debounced parameter object — fires the planner whenever the
  // operator changes anything. 500 ms matches the spec §C.1
  // recommendation.
  const planParams = useMemo(
    () => ({
      source: {
        source_type: sourceType,
        tags: sourceType === 'specific_tags' ? tagsList : [],
        station_codes:
          sourceType === 'station_codes' ? stationsList : [],
        date_from: dateFrom || null,
        date_to: dateTo || null,
      },
      target_configs: targetConfigs,
      include_failed_categories: includeFailed,
    }),
    [
      sourceType,
      tagsList,
      stationsList,
      dateFrom,
      dateTo,
      targetConfigs,
      includeFailed,
    ],
  );
  const debouncedParams = useDebounce(planParams, 500);

  // Plan preview — re-fires after 500 ms of no input change.
  const [plan, setPlan] = useState<BackfillPlanResponse | null>(null);
  const [planLoading, setPlanLoading] = useState<boolean>(false);
  const [planError, setPlanError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const isEmptySource =
      (debouncedParams.source.source_type === 'specific_tags' &&
        debouncedParams.source.tags.length === 0) ||
      (debouncedParams.source.source_type === 'station_codes' &&
        debouncedParams.source.station_codes.length === 0);
    if (isEmptySource) {
      setPlan(null);
      setPlanError(null);
      setPlanLoading(false);
      return;
    }
    setPlanLoading(true);
    api
      .planBackfill(debouncedParams)
      .then((resp) => {
        if (cancelled) return;
        setPlan(resp);
        setPlanError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? `Ошибка ${err.status}: ${err.message}`
            : err instanceof Error
              ? err.message
              : 'не удалось загрузить превью';
        setPlanError(msg);
        setPlan(null);
      })
      .finally(() => {
        if (!cancelled) setPlanLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedParams]);

  // Submit — runs the planner one more time on the server inside
  // the route handler, so the «what we'll create» count can't drift
  // between preview and run. The Mini App treats `is_large_plan`
  // as a confirmation gate: ask, then resubmit with `confirm=true`.
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async () => {
    if (!plan || plan.total_planned_tasks === 0) return;
    if (
      plan.is_large_plan &&
      !window.confirm(
        `Большая серия: будет создано ${plan.total_planned_tasks} задач (` +
          `${formatDuration(plan.estimated_duration_min)}). Запустить?`,
      )
    ) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await api.runBackfill({
        ...debouncedParams,
        tag: effectiveTag,
        confirm: true,
      });
      onCreated(resp);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Ошибка ${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'не удалось запустить серию';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting &&
    plan !== null &&
    plan.total_planned_tasks > 0 &&
    targetConfigs.length > 0 &&
    effectiveTag.length > 0;

  return (
    <div data-testid="backfill-mode-form">
      <div className="modal-field">
        <label>Что восполняем</label>
        <div className="modal-field-radio">
          <label className="radio-row">
            <input
              type="radio"
              name="backfill-source"
              checked={sourceType === 'all_dates_with_done'}
              onChange={() => setSourceType('all_dates_with_done')}
              className="gnss-qa-radio"
            />
            <span>Все станции в диапазоне дат</span>
          </label>
          <label className="radio-row">
            <input
              type="radio"
              name="backfill-source"
              checked={sourceType === 'specific_tags'}
              onChange={() => setSourceType('specific_tags')}
              className="gnss-qa-radio"
            />
            <span>Только серии (теги через запятую)</span>
          </label>
          {sourceType === 'specific_tags' && (
            <textarea
              rows={2}
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="Random25_..., night_smoke_..., ..."
              data-testid="backfill-tags-input"
              className="gnss-qa-input"
            />
          )}
          <label className="radio-row">
            <input
              type="radio"
              name="backfill-source"
              checked={sourceType === 'station_codes'}
              onChange={() => setSourceType('station_codes')}
              className="gnss-qa-radio"
            />
            <span>По кодам станций</span>
          </label>
          {sourceType === 'station_codes' && (
            <textarea
              rows={2}
              value={stationsRaw}
              onChange={(e) => setStationsRaw(e.target.value)}
              placeholder="AMC4, BOR1, POTS..."
              data-testid="backfill-stations-input"
              className="gnss-qa-input"
            />
          )}
        </div>
      </div>

      <div className="modal-field">
        <label>Диапазон дат (опционально)</label>
        <div className="date-range">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="backfill-date-from"
            className="gnss-qa-date"
          />
          <span className="date-range-sep">…</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="backfill-date-to"
            className="gnss-qa-date"
          />
        </div>
        <span className="hint">
          Пусто — все даты с DONE задачами в выбранном источнике.
        </span>
      </div>

      <div className="modal-field">
        <label>Какие конфиги восполнять</label>
        <div className="chip-row">
          {TARGET_CONFIG_OPTIONS.map((cfg) => {
            const active = targetConfigs.includes(cfg.value);
            return (
              <button
                key={cfg.value}
                type="button"
                className={active ? 'chip active' : 'chip'}
                onClick={() =>
                  setTargetConfigs((curr) =>
                    active
                      ? curr.filter((c) => c !== cfg.value)
                      : [...curr, cfg.value],
                  )
                }
                data-testid={`backfill-config-${cfg.value}`}
              >
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="modal-field">
        <label>Что делать с FAILED</label>
        <div className="modal-field-radio">
          <label className="radio-row">
            <input
              type="radio"
              name="backfill-failed-mode"
              checked={includeFailed.length === 0}
              onChange={() => setIncludeFailed([])}
              className="gnss-qa-radio"
            />
            <span>Только не запускали (рекомендуется)</span>
          </label>
          <label className="radio-row">
            <input
              type="radio"
              name="backfill-failed-mode"
              checked={includeFailed.length > 0}
              onChange={() =>
                setIncludeFailed(
                  includeFailed.length > 0 ? includeFailed : [],
                )
              }
              className="gnss-qa-radio"
            />
            <span>Также повторить failed-категории:</span>
          </label>
          <div className="backfill-failed-checkboxes">
            {RETRYABLE_FAILED_CATEGORIES.map((cat) => (
              <label key={cat.value} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={includeFailed.includes(cat.value)}
                  onChange={(e) =>
                    setIncludeFailed((curr) =>
                      e.target.checked
                        ? [...curr, cat.value]
                        : curr.filter((c) => c !== cat.value),
                    )
                  }
                  data-testid={`backfill-include-${cat.value}`}
                  className="gnss-qa-checkbox"
                />
                <span>{cat.label}</span>
              </label>
            ))}
            {/* Spec §C.1 — these two categories are deterministic;
                re-running them lands the same FAILED. Disabled
                checkboxes show the operator WHY they can't opt in. */}
            {DETERMINISTIC_FAILED_CATEGORIES.map((cat) => (
              <label
                key={cat.value}
                className="checkbox-row disabled"
                title="Нельзя — детерминированный результат, повторный запуск даст ту же ошибку"
              >
                <input
                  type="checkbox"
                  checked={false}
                  disabled
                  data-testid={`backfill-include-${cat.value}`}
                  className="gnss-qa-checkbox"
                />
                <span>{cat.label} — нельзя</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="modal-field">
        <label>Тег новой серии</label>
        <input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          placeholder={computedDefaultTag}
          data-testid="backfill-tag-input"
          className="gnss-qa-input"
        />
        <span className="hint">
          Пусто — авто-генерация: <code>{computedDefaultTag}</code>
        </span>
      </div>

      <div
        className="backfill-preview"
        data-testid="backfill-preview"
      >
        <div className="bsfc-section-title">ПРЕВЬЮ ПЛАНА</div>
        {planLoading && (
          <div data-testid="backfill-preview-loading">обновляю…</div>
        )}
        {planError && (
          <div className="modal-error" role="alert">
            {planError}
          </div>
        )}
        {!planLoading && !planError && plan === null && (
          <div data-testid="backfill-preview-empty-source">
            Укажи источник (теги или коды станций).
          </div>
        )}
        {plan && plan.total_planned_tasks === 0 && (
          <div data-testid="backfill-preview-empty-plan">
            Нечего восполнять — все запрошенные конфиги уже отработаны
            или исключены по правилам.
          </div>
        )}
        {plan && plan.total_planned_tasks > 0 && (
          <div data-testid="backfill-preview-summary">
            <div>
              Будет создано <strong>{plan.total_planned_tasks}</strong> задач
            </div>
            <div>
              Покрытие: {plan.covered_pairs} пар × {plan.covered_dates} дат
            </div>
            <div>
              Ожидаемое время:{' '}
              <strong>{formatDuration(plan.estimated_duration_min)}</strong>
            </div>
            {plan.is_large_plan && (
              <div
                className="modal-warning"
                role="status"
                data-testid="backfill-large-plan-warning"
              >
                Большая серия (&gt; 500 задач). При запуске спросим
                подтверждение.
              </div>
            )}
            {Object.keys(plan.breakdown_by_reason_for_excluded).length > 0 && (
              <ul className="backfill-exclusion-list">
                {Object.entries(plan.breakdown_by_reason_for_excluded).map(
                  ([reason, count]) => (
                    <li
                      key={reason}
                      data-testid={`backfill-excluded-${reason}`}
                    >
                      {EXCLUSION_REASON_LABELS[reason] ?? reason}:{' '}
                      <strong>{count}</strong>
                    </li>
                  ),
                )}
              </ul>
            )}
          </div>
        )}
      </div>

      {submitError && (
        <div className="modal-error" role="alert">
          {submitError}
        </div>
      )}
      <div className="modal-actions">
        <button
          type="button"
          className="primary"
          onClick={onSubmit}
          disabled={!canSubmit}
          data-testid="backfill-submit"
        >
          {submitting ? 'Запускаю…' : 'Запустить →'}
        </button>
      </div>
    </div>
  );
}
